import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, hr } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Download, FileSpreadsheet, FileText, ClipboardList, RefreshCw } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HR_MONTHS = [
  { v: 1, l: "Siječanj" }, { v: 2, l: "Veljača" }, { v: 3, l: "Ožujak" },
  { v: 4, l: "Travanj" }, { v: 5, l: "Svibanj" }, { v: 6, l: "Lipanj" },
  { v: 7, l: "Srpanj" }, { v: 8, l: "Kolovoz" }, { v: 9, l: "Rujan" },
  { v: 10, l: "Listopad" }, { v: 11, l: "Studeni" }, { v: 12, l: "Prosinac" },
];

export default function PayrollReportCard() {
  const now = new Date();
  const [mode, setMode] = useState("month"); // month | range | year
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Iter112: sort_by — "first_name" (default, po početnom slovu imena) ili
  // "last_name" (po početnom slovu prezimena). Perzistira se preko queryString-a
  // pa vrijedi za JSON view, CSV, XLSX i PDF izvoze.
  const [sortBy, setSortBy] = useState("first_name");
  const [startDate, setStartDate] = useState(() => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ mode });
    if (mode === "month") { p.set("year", year); p.set("month", month); }
    if (mode === "range") { p.set("start", startDate); p.set("end", endDate); }
    if (mode === "year") { p.set("year", year); }
    p.set("sort_by", sortBy);
    return p.toString();
  }, [mode, year, month, startDate, endDate, sortBy]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/reports/vacation-payroll?${queryString}`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Greška pri učitavanju izvještaja");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { load(); }, [load]);

  const downloadFile = async (fmt) => {
    try {
      const url = `${REACT_APP_BACKEND_URL}/api/reports/vacation-payroll.${fmt}?${queryString}`;
      // Use fetch with credentials so cookie auth is preserved
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const dispo = resp.headers.get("Content-Disposition") || "";
      const match = dispo.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `GO_izvjestaj.${fmt}`;
      const a = document.createElement("a");
      const bloburl = URL.createObjectURL(blob);
      a.href = bloburl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(bloburl);
      toast.success(`${fmt.toUpperCase()} preuzet: ${fname}`);
    } catch (e) {
      toast.error(`Greška pri preuzimanju ${fmt.toUpperCase()}: ${e.message}`);
    }
  };

  const openPdfPreview = () => {
    const url = `${REACT_APP_BACKEND_URL}/api/reports/vacation-payroll.pdf?${queryString}`;
    window.open(url, "_blank", "noopener");
  };

  const yearOptions = useMemo(() => {
    const cy = now.getFullYear();
    return [cy - 1, cy, cy + 1];
  }, [now]);

  return (
    <Card data-testid="payroll-report-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-accent" />
            <CardTitle className="text-base">Izvještaj za dekanat / obradu plaće</CardTitle>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => downloadFile("csv")} data-testid="report-download-csv">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button size="sm" variant="secondary" onClick={() => downloadFile("xlsx")} data-testid="report-download-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button size="sm" onClick={openPdfPreview} data-testid="report-preview-pdf">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Uključeni su samo zaposlenici s <b>izdanim rješenjem</b> (statusi rješenje / poslano) koji koriste GO u odabranom razdoblju.
          Dani unutar kolektivnog godišnjeg odmora se ne broje dvaput u dodatnim periodima.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter row */}
        <div className="flex flex-wrap items-end gap-3 border-b pb-3">
          <div className="w-40">
            <label className="text-xs font-medium block mb-1">Način</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="report-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Mjesec</SelectItem>
                <SelectItem value="range">Datumski raspon</SelectItem>
                <SelectItem value="year">Cijela godina</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "month" && (
            <>
              <div className="w-40">
                <label className="text-xs font-medium block mb-1">Mjesec</label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger data-testid="report-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HR_MONTHS.map(m => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <label className="text-xs font-medium block mb-1">Godina</label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger data-testid="report-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {mode === "range" && (
            <>
              <div>
                <label className="text-xs font-medium block mb-1">Od</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="report-start-input" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Do</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="report-end-input" />
              </div>
            </>
          )}

          {mode === "year" && (
            <div className="w-28">
              <label className="text-xs font-medium block mb-1">Godina</label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger data-testid="report-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="w-44">
            <label className="text-xs font-medium block mb-1">Sortiranje</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="report-sortby-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first_name" data-testid="sortby-first-name">Po imenu (A-Ž)</SelectItem>
                <SelectItem value="last_name" data-testid="sortby-last-name">Po prezimenu (A-Ž)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="report-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Osvježi
          </Button>
        </div>

        {err && <p className="text-sm text-red-600">{err}</p>}

        {data && (
          <>
            {/* Summary */}
            <div className="flex flex-wrap gap-2 items-baseline">
              <h3 className="serif text-lg font-semibold">{data.label}</h3>
              <Badge variant="secondary" data-testid="report-totals-employees">{data.totals.employees} zaposlenika</Badge>
              <Badge variant="secondary" data-testid="report-totals-workdays">{data.totals.workdays} radnih dana</Badge>
              {data.totals.collective_workdays > 0 && (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Kolektivni: {data.totals.collective_workdays}</Badge>
              )}
              {data.totals.additional_workdays > 0 && (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Dodatni: {data.totals.additional_workdays}</Badge>
              )}
              <Badge variant="outline">{data.totals.requests} rješenja</Badge>
            </div>

            {/* Grouped table */}
            {data.groups.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-4">
                Nema zaposlenika s izdanim rješenjem u odabranom razdoblju.
              </p>
            ) : (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm" data-testid="report-groups-table">
                  <thead className="bg-primary text-primary-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Zaposlenik</th>
                      <th className="text-left px-3 py-2">Katedra</th>
                      <th className="text-left px-3 py-2">Rješenja (KLASA)</th>
                      <th className="text-right px-3 py-2">Dana u razdoblju</th>
                      <th className="text-right px-3 py-2">Kol.</th>
                      <th className="text-right px-3 py-2">Dod.</th>
                      <th className="text-right px-3 py-2">Prijenos isk.</th>
                      <th className="text-right px-3 py-2">Preostalo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map(g => (
                      <tr key={g.employee_email} className="border-t hover:bg-secondary/40" data-testid={`report-group-${g.employee_email}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{g.employee_name}</div>
                          <div className="text-[11px] text-muted-foreground">{g.employee_email}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">{g.katedra || "—"}</td>
                        <td className="px-3 py-2 text-xs">
                          {g.requests.map(r => (
                            <div key={r.id} className="whitespace-nowrap">
                              <span className="font-mono">{r.klasa || "—"}</span>
                              {r.decision_issued_at && (
                                <span className="text-muted-foreground ml-1">· {hr(r.decision_issued_at)}</span>
                              )}
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">{g.workdays_in_range}</td>
                        <td className="px-3 py-2 text-right text-blue-700">{g.collective_days_in_range}</td>
                        <td className="px-3 py-2 text-right text-amber-700">{g.additional_days_in_range}</td>
                        <td className="px-3 py-2 text-right">{g.prev_year_used}</td>
                        <td className="px-3 py-2 text-right">{g.remaining_fund}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-secondary/60 font-semibold">
                      <td className="px-3 py-2" colSpan={3}>UKUPNO</td>
                      <td className="px-3 py-2 text-right">{data.totals.workdays}</td>
                      <td className="px-3 py-2 text-right text-blue-700">{data.totals.collective_workdays}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{data.totals.additional_workdays}</td>
                      <td className="px-3 py-2 text-right"></td>
                      <td className="px-3 py-2 text-right"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Details per period (flat) */}
            {data.rows.length > 0 && (
              <details className="border rounded-md">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium bg-secondary/40 hover:bg-secondary/60">
                  Detalji po periodima ({data.rows.length})
                </summary>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="report-rows-table">
                    <thead className="bg-secondary/60">
                      <tr>
                        <th className="text-left px-2 py-1.5">Zaposlenik</th>
                        <th className="text-left px-2 py-1.5">KLASA / URBROJ</th>
                        <th className="text-left px-2 py-1.5">Datum rješenja</th>
                        <th className="text-left px-2 py-1.5">Tip</th>
                        <th className="text-left px-2 py-1.5">Od</th>
                        <th className="text-left px-2 py-1.5">Do</th>
                        <th className="text-right px-2 py-1.5">Dana (ukupno)</th>
                        <th className="text-right px-2 py-1.5">Dana u razdoblju</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r, i) => (
                        <tr key={`${r.request_id}-${i}`} className="border-t">
                          <td className="px-2 py-1.5">{r.employee_name}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{r.klasa}<br/>{r.urbroj}</td>
                          <td className="px-2 py-1.5">{hr(r.decision_issued_at)}</td>
                          <td className="px-2 py-1.5">
                            <Badge variant="outline" className={r.period_type === "collective" ? "text-blue-700" : "text-amber-700"}>
                              {r.period_type === "collective" ? "Kolektivni" : "Dodatni"}
                            </Badge>
                          </td>
                          <td className="px-2 py-1.5">{hr(r.period_start)}</td>
                          <td className="px-2 py-1.5">{hr(r.period_end)}</td>
                          <td className="px-2 py-1.5 text-right">{r.workdays_full}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{r.workdays_in_range}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* By month table (year mode only) */}
            {mode === "year" && (
              <div className="border rounded-md overflow-hidden">
                <div className="px-3 py-2 text-sm font-medium bg-secondary/40">Po mjesecima</div>
                <table className="w-full text-xs" data-testid="report-bymonth-table">
                  <thead className="bg-secondary/20">
                    <tr>
                      <th className="text-left px-2 py-1.5">Mjesec</th>
                      <th className="text-right px-2 py-1.5">Zaposlenika s GO</th>
                      <th className="text-right px-2 py-1.5">Radnih dana</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_month.map(m => (
                      <tr key={m.month} className={`border-t ${m.workdays > 0 ? "" : "text-muted-foreground"}`}>
                        <td className="px-2 py-1.5 capitalize">{m.month_name}</td>
                        <td className="px-2 py-1.5 text-right">{m.employees_count}</td>
                        <td className="px-2 py-1.5 text-right">{m.workdays}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
